import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@/lib/api/client';
import { profileApi } from '@/lib/api/endpoints';
import type { CanonicalProfilePayload, ProfileHealth } from '@/lib/api/types';
import { useToast } from '@/hooks/useToast';

export type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

export type CanonicalProfile = Record<string, unknown> & {
  account?: Record<string, unknown>;
  personal?: {
    kz?: Record<string, string>;
    ru?: Record<string, string>;
    en?: Record<string, string>;
    country?: string;
    city?: string;
    timezone?: string;
    avatarUrl?: string;
    avatarRotation?: number;
    avatarZoom?: number;
  };
  academic?: Record<string, unknown>;
  education?: Array<Record<string, unknown>>;
  work?: Array<Record<string, unknown>>;
  publications?: Array<Record<string, unknown>>;
  projects?: Array<Record<string, unknown>>;
  achievements?: Array<Record<string, unknown>>;
  documents?: Array<Record<string, unknown>>;
  privacy?: Record<string, unknown>;
  doctoral_completion?: Record<string, unknown>;
  settings?: Record<string, unknown>;
};

function deepSet(obj: CanonicalProfile, path: string, value: unknown): CanonicalProfile {
  const parts = path.split('.');
  const next = structuredClone(obj) as CanonicalProfile;
  let cursor: Record<string, unknown> = next as Record<string, unknown>;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    const child = cursor[key];
    if (!child || typeof child !== 'object') {
      cursor[key] = /^\d+$/.test(parts[i + 1]) ? [] : {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  }
  cursor[parts[parts.length - 1]] = value;
  return next;
}

export function useCanonicalProfile() {
  const { notify } = useToast();
  const [profile, setProfile] = useState<CanonicalProfile | null>(null);
  const [health, setHealth] = useState<ProfileHealth | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const profileRef = useRef<CanonicalProfile | null>(null);
  const dirtyRoots = useRef(new Set<string>());
  const persistQueue = useRef<Promise<boolean>>(Promise.resolve(true));
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistOnLeave = useRef<(() => void) | null>(null);

  const applyPayload = useCallback((payload: CanonicalProfilePayload) => {
    const next = payload.profile as CanonicalProfile;
    profileRef.current = next;
    setProfile(next);
    setHealth(payload.health);
    setUpdatedAt(payload.updated_at);
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await profileApi.get();
      applyPayload(payload);
      setSaveState('saved');
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Не удалось загрузить профиль';
      notify(message, 'error');
      setSaveState('error');
    } finally {
      setLoading(false);
    }
  }, [applyPayload, notify]);

  useEffect(() => {
    void reload();
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      persistOnLeave.current?.();
    };
  }, [reload]);

  const persist = useCallback(
    (_patch: Record<string, unknown>) => {
      const operation = persistQueue.current
        .catch(() => false)
        .then(async () => {
          const current = profileRef.current;
          if (!current || !dirtyRoots.current.size) return true;
          const patch = Object.fromEntries(
            [...dirtyRoots.current].map((key) => [key, structuredClone(current[key])]),
          );
          setSaveState('saving');
          try {
            const payload = await profileApi.patch(patch);
            Object.entries(patch).forEach(([key, value]) => {
              if (JSON.stringify(profileRef.current?.[key]) === JSON.stringify(value))
                dirtyRoots.current.delete(key);
            });
            const merged = { ...payload.profile } as CanonicalProfile;
            dirtyRoots.current.forEach((key) => {
              merged[key] = profileRef.current?.[key];
            });
            applyPayload({ ...payload, profile: merged });
            setSaveState(dirtyRoots.current.size ? 'dirty' : 'saved');
            return true;
          } catch (error) {
            const message = error instanceof ApiError ? error.message : 'Ошибка сохранения';
            notify(message, 'error');
            setSaveState('error');
            return false;
          }
        });
      persistQueue.current = operation;
      return operation;
    },
    [applyPayload, notify],
  );

  persistOnLeave.current = () => {
    if (dirtyRoots.current.size) void persist({});
  };

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dirtyRoots.current.size) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  const setField = useCallback(
    (path: string, value: unknown) => {
      dirtyRoots.current.add(path.split('.')[0]);
      setProfile((prev) => {
        if (!prev) return prev;
        const next = deepSet(prev, path, value);
        profileRef.current = next;
        return next;
      });
      setSaveState('dirty');
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      autosaveTimer.current = setTimeout(() => {
        const current = profileRef.current;
        if (!current) return;
        // Persist full current profile snapshot for the touched top-level key.
        const top = path.split('.')[0];
        void persist({ [top]: current[top as keyof CanonicalProfile] });
      }, 900);
    },
    [persist],
  );

  const setSection = useCallback(
    (sectionKey: string, value: unknown) => {
      dirtyRoots.current.add(sectionKey);
      setProfile((prev) => {
        if (!prev) return prev;
        const next = { ...prev, [sectionKey]: value } as CanonicalProfile;
        profileRef.current = next;
        return next;
      });
      setSaveState('dirty');
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      autosaveTimer.current = setTimeout(() => {
        void persist({ [sectionKey]: value });
      }, 900);
    },
    [persist],
  );

  const saveNow = useCallback(async () => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    const current = profileRef.current;
    if (!current) return false;
    return persist(current as Record<string, unknown>);
  }, [persist]);

  const sectionPct = useCallback((id: string) => health?.section_scores?.[id] ?? 0, [health]);

  return {
    profile,
    health,
    updatedAt,
    loading,
    saveState,
    setField,
    setSection,
    saveNow,
    reload,
    sectionPct,
  };
}
